import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import {
  ComponentProps,
  HTMLAttributes,
  KeyboardEvent,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button as AriaButton } from 'react-aria-components';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from 'react-feather';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { DataStoreImage as DataStoreImg } from 'components/Image';
import { QueryResolver } from 'components/QueryResolver';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Dialog, DialogBody, DialogTitle } from 'components/ui/dialog';
import { EstimateService } from 'services/api/estimates/endpoints';
import { UtilityService } from 'services/api/utilities/endpoint';
import { DataStoreImage } from 'services/api/utilities/types';
import { cn } from 'utils/helpers';
import { useLocale } from 'utils/hooks/useLocale';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

function CarouselCount() {
  const { api } = useCarousel();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <Badge>
      {current}/{count}
    </Badge>
  );
}
const Carousel = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const onSelect = useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative h-full', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="h-full overflow-hidden">
      <div
        ref={ref}
        className={cn(
          'flex h-full',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          'min-w-0 shrink-0 grow-0 basis-full h-full',
          orientation === 'horizontal' ? 'pl-4' : 'pt-4',
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof Button>
>(({ className, ...props }, ref) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      ref={ref}
      className={cn(className)}
      isDisabled={!canScrollPrev}
      onClick={scrollPrev}
    >
      <ChevronLeftIcon data-slot="icon" />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
});
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof Button>
>(({ className, ...props }, ref) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      ref={ref}
      className={cn(className)}
      isDisabled={!canScrollNext}
      onClick={scrollNext}
    >
      <ChevronRightIcon data-slot="icon" />
      <span className="sr-only">Next slide</span>
    </Button>
  );
});
CarouselNext.displayName = 'CarouselNext';

export default function WorkRequestAttachments({
  workRequestId,
}: {
  workRequestId: number;
}) {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = UtilityService.endpoints.images.useQuery(
    {
      params: { workRequestId },
      purpose: 'WorkRequest',
    },
    { skip: workRequestId === 0 },
  );
  const combinedSearchParams = new URLSearchParams({
    ...Object.fromEntries(searchParams.entries()),
    modal: 'ATTACHMENTS_PHOTO_GALLERY',
  });

  return (
    <fieldset className="flex w-full min-w-0 flex-col space-y-2 overflow-hidden">
      <div className="flex items-center justify-between">
        <legend className="font-prompt text-lg font-semibold">
          {t('Attachments')}
        </legend>
      </div>
      <div className="flex flex-col overflow-hidden">
        <QueryResolver query={query}>
          {(attachments) => (
            <div>
              {attachments.length === 0 ? (
                <div className="text-base font-semibold text-gray-300">
                  {t('attachments_empty')}
                </div>
              ) : (
                <>
                  <ul className="flex space-x-4 overflow-x-auto">
                    {attachments.map((image) => (
                      <li
                        key={image.id}
                        className="relative h-28 w-28 shrink-0 overflow-hidden rounded"
                      >
                        <AriaButton
                          className="group h-full w-full transition"
                          onPress={() => {
                            navigate({
                              pathname: '.',
                              search: `?${combinedSearchParams.toString()}&attachment_id=${
                                image.id
                              }`,
                            });
                          }}
                        >
                          <DataStoreImg
                            imageId={Number(image.id)}
                            className="h-full w-full object-cover group-data-[hovered]:opacity-75"
                          />
                        </AriaButton>
                      </li>
                    ))}
                  </ul>
                  <ImageGallery
                    images={attachments}
                    workRequestId={workRequestId}
                  />
                </>
              )}
            </div>
          )}
        </QueryResolver>
      </div>
    </fieldset>
  );
}

function ImageGallery({
  images,
  workRequestId,
}: {
  images: DataStoreImage[];
  workRequestId: number;
}) {
  const [searchParams] = useSearchParams();
  const open = searchParams.get('modal') === 'ATTACHMENTS_PHOTO_GALLERY';
  const attachment_id = z.coerce
    .number()
    .catch(0)
    .parse(searchParams.get('attachment_id'));
  const initialIndex = attachment_id
    ? images.findIndex((img) => img.id === attachment_id)
    : 0;

  const query = EstimateService.endpoints.estimate.useQuery(workRequestId, {
    skip: !open,
  });
  const navigate = useNavigate();

  const workType = query.data?.workRequestDetails.workType ?? '';
  const dimissSearchParams = useMemo(() => {
    searchParams.delete('modal');
    searchParams.delete('attachment_id');
    return new URLSearchParams({
      ...Object.fromEntries(searchParams.entries()),
    });
  }, [searchParams]);

  return (
    <Dialog
      size="5xl"
      onOpenChange={(isOpen) => {
        !isOpen &&
          navigate({
            pathname: '.',
            search: `?${dimissSearchParams.toString()}`,
          });
      }}
      isOpen={open}
    >
      <DialogTitle>Attachments for {workType}</DialogTitle>
      <DialogBody>
        <div className="aspect-h-9 aspect-w-16 h-full w-full bg-zinc-900/20">
          <div>
            <Carousel opts={{ startIndex: initialIndex }}>
              <CarouselContent>
                {images.map((image) => (
                  <CarouselItem key={image.id}>
                    <DataStoreImg
                      imageId={image.id}
                      className="h-full w-full object-contain"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="absolute inset-y-0 right-0 p-1.5">
                <CarouselCount />
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center p-1.5">
                <CarouselNext />
              </div>
              <div className="next-0 absolute inset-y-0 flex items-center p-1.5">
                <CarouselPrevious />
              </div>
            </Carousel>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}
